import { useRef, useState } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * Local-file JSONL ingest — the simple, robust path.
 *
 * The browser reads the JSONL file line-by-line using the streaming
 * FileReader API, groups records into batches of BATCH_SIZE, and POSTs
 * each batch to /api/admin/corpus/ingest-batch. Each POST is a short
 * request (a few hundred KB), so there is:
 *   - no dependence on the pod having outbound internet
 *   - no long-running background task that can silently die
 *   - no 10-hour timeouts
 *   - live progress updates every batch (~2–5 seconds)
 *   - if a single batch fails, we retry it up to 2 times before giving
 *     up; on re-run of the whole file, the server upserts on case_id so
 *     already-ingested rows are updated in place (no duplicates)
 *
 * Sit-back UX. Pick file → click Start → watch the bar.
 */

const BATCH_SIZE = 250;
const MAX_RETRIES_PER_BATCH = 2;
const CHUNK_BYTES = 256 * 1024;   // 256 KB reads

export default function AdminCorpusJsonlFileSection() {
  const [file, setFile] = useState(null);
  const [datasetName, setDatasetName] = useState("curated_gold_ge9_persona");
  const [minScore, setMinScore] = useState(9.0);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({
    parsed: 0, ingested: 0, skipped: 0, failed: 0,
    batchesDone: 0, currentBatch: 0, pct: 0,
    bytesRead: 0, totalBytes: 0,
    elapsedMs: 0, startedAt: null,
    lastError: "",
  });
  const stopRef = useRef(false);

  function reset() {
    stopRef.current = false;
    setProgress({
      parsed: 0, ingested: 0, skipped: 0, failed: 0,
      batchesDone: 0, currentBatch: 0, pct: 0,
      bytesRead: 0, totalBytes: file ? file.size : 0,
      elapsedMs: 0, startedAt: null, lastError: "",
    });
  }

  async function postBatch(records) {
    let attempt = 0;
    while (attempt <= MAX_RETRIES_PER_BATCH) {
      try {
        const r = await fetch(`${API}/admin/corpus/ingest-batch`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            records,
            dataset_name: datasetName.trim() || "curated_gold_ge9_persona",
            importance: 0.95,
            min_score: Number(minScore) || 9.0,
          }),
        });
        if (r.ok) return await r.json();
        // Retryable on 5xx and 429; give up on 4xx
        if (r.status >= 500 || r.status === 429) {
          attempt += 1;
          await new Promise((res) => setTimeout(res, 1500 * attempt));
          continue;
        }
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${r.status}`);
      } catch (e) {
        attempt += 1;
        if (attempt > MAX_RETRIES_PER_BATCH) throw e;
        await new Promise((res) => setTimeout(res, 1500 * attempt));
      }
    }
    throw new Error("batch failed after retries");
  }

  async function start() {
    if (!file) {
      toast.error("Pick the JSONL file first.");
      return;
    }
    reset();
    setRunning(true);
    stopRef.current = false;
    const startedAt = Date.now();
    setProgress((p) => ({
      ...p, startedAt, totalBytes: file.size,
    }));

    let parsed = 0;
    let ingested = 0;
    let skipped = 0;
    let failed = 0;
    let batchesDone = 0;
    let currentBatch = [];
    let bytesRead = 0;
    let leftover = "";

    const decoder = new TextDecoder("utf-8");
    // File.stream() returns a ReadableStream of Uint8Array — supported
    // in all modern browsers. This is the only sane way to read a
    // 200MB file without holding it all in memory.
    const reader = file.stream().getReader();

    const flushBatch = async () => {
      if (!currentBatch.length) return;
      const batch = currentBatch;
      currentBatch = [];
      try {
        const res = await postBatch(batch);
        ingested += res.ingested || 0;
        skipped += (res.skipped_lowscore || 0) + (res.skipped_missing || 0);
        failed += res.failed || 0;
      } catch (e) {
        failed += batch.length;
        setProgress((p) => ({ ...p, lastError: e?.message || String(e) }));
      }
      batchesDone += 1;
    };

    const processLine = (line) => {
      const t = line.trim();
      if (!t) return;
      try {
        const rec = JSON.parse(t);
        currentBatch.push(rec);
        parsed += 1;
      } catch {
        skipped += 1;
      }
    };

    try {
      while (true) {
        if (stopRef.current) break;
        const { value, done } = await reader.read();
        if (done) break;
        bytesRead += value.byteLength;
        leftover += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = leftover.indexOf("\n")) !== -1) {
          const line = leftover.slice(0, idx);
          leftover = leftover.slice(idx + 1);
          processLine(line);
          if (currentBatch.length >= BATCH_SIZE) {
            await flushBatch();
            setProgress({
              parsed, ingested, skipped, failed,
              batchesDone, currentBatch: currentBatch.length,
              pct: file.size ? Math.min(99, Math.round((bytesRead / file.size) * 100)) : 0,
              bytesRead, totalBytes: file.size,
              elapsedMs: Date.now() - startedAt, startedAt,
              lastError: "",
            });
          }
        }
      }
      // final tail
      const tail = (leftover + decoder.decode()).trim();
      if (tail) processLine(tail);
      if (currentBatch.length) await flushBatch();

      setProgress({
        parsed, ingested, skipped, failed,
        batchesDone, currentBatch: 0,
        pct: 100, bytesRead: file.size, totalBytes: file.size,
        elapsedMs: Date.now() - startedAt, startedAt,
        lastError: "",
      });
      toast.success(
        stopRef.current
          ? `Stopped. Ingested ${ingested.toLocaleString()} rows.`
          : `Done. Ingested ${ingested.toLocaleString()} of ${parsed.toLocaleString()} rows.`,
      );
    } catch (e) {
      toast.error(`Ingest crashed: ${e?.message || e}`);
      setProgress((p) => ({ ...p, lastError: e?.message || String(e) }));
    } finally {
      setRunning(false);
    }
  }

  function stop() {
    stopRef.current = true;
    toast("Stopping after the current batch…");
  }

  const p = progress;
  const rate = p.startedAt && p.elapsedMs > 500
    ? Math.round((p.parsed / (p.elapsedMs / 1000)) || 0)
    : 0;
  const etaSec = rate > 0 && p.totalBytes > 0
    ? Math.round(((p.totalBytes - p.bytesRead) / (p.bytesRead || 1)) * (p.elapsedMs / 1000))
    : 0;

  return (
    <div
      className="border border-[var(--border)] p-5 space-y-4"
      data-testid="corpus-jsonl-file-panel"
    >
      <div>
        <p className="font-ui text-sm text-[var(--text)] leading-relaxed">
          <strong>The simple way.</strong> Pick your JSONL file below and hit
          Start. Your browser reads it in chunks and streams it to the server
          {" "}<span className="font-mono-ui text-[11px]">
            250 rows at a time
          </span>. No background job. No URL fetch. Progress updates every
          few seconds. If a batch fails, you&apos;ll see it here. Safe to re-run —
          duplicates are updated in place, never doubled.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="md:col-span-3 flex flex-col gap-1.5">
          <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            JSONL file
          </span>
          <input
            type="file"
            accept=".jsonl,.json,application/json,application/x-ndjson,text/plain"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={running}
            data-testid="corpus-jsonl-file-input"
            className="text-sm text-[var(--text)] file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[var(--accent)] file:text-[var(--bg)] file:font-mono-ui file:text-[10px] file:tracking-[0.24em] file:uppercase hover:file:opacity-90 disabled:opacity-50"
          />
          {file && (
            <span className="font-mono-ui text-[10px] tracking-[0.14em] uppercase text-[var(--text-dim)]">
              {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
            </span>
          )}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            Dataset name
          </span>
          <input
            type="text"
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            disabled={running}
            data-testid="corpus-jsonl-file-dataset-input"
            className="bg-transparent border border-[var(--border)] px-3 py-2 font-mono-ui text-[12px] text-[var(--text)] focus:border-[var(--accent)] focus:outline-none disabled:opacity-50"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            Min score
          </span>
          <input
            type="number"
            step="0.1"
            min="0"
            max="10"
            value={minScore}
            onChange={(e) => setMinScore(e.target.value)}
            disabled={running}
            data-testid="corpus-jsonl-file-minscore-input"
            className="bg-transparent border border-[var(--border)] px-3 py-2 font-mono-ui text-[12px] text-[var(--text)] focus:border-[var(--accent)] focus:outline-none disabled:opacity-50"
          />
        </label>
      </div>

      <div className="flex items-center justify-end gap-3">
        {running && (
          <button
            onClick={stop}
            data-testid="corpus-jsonl-file-stop"
            className="px-4 py-2.5 border border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text)] font-mono-ui text-[10px] tracking-[0.28em] uppercase"
          >
            Stop
          </button>
        )}
        <button
          onClick={start}
          disabled={running || !file}
          data-testid="corpus-jsonl-file-start"
          className="px-5 py-2.5 bg-[var(--accent)] text-[var(--bg)] hover:opacity-90 font-mono-ui text-[10px] tracking-[0.28em] uppercase transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {running ? "Streaming…" : "Start ingest"}
        </button>
      </div>

      {/* Progress bar */}
      <div>
        <div className="h-1.5 bg-[var(--border)] overflow-hidden">
          <div
            className="h-full bg-[var(--accent)] transition-all duration-300"
            style={{ width: `${p.pct}%` }}
            data-testid="corpus-jsonl-file-progress-bar"
          />
        </div>
        <div className="flex items-center justify-between mt-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
          <span>
            {(p.bytesRead / (1024 * 1024)).toFixed(1)} MB /{" "}
            {(p.totalBytes / (1024 * 1024)).toFixed(1)} MB
          </span>
          <span>{p.pct}%</span>
        </div>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <StatBox label="Parsed" value={p.parsed.toLocaleString()} testid="corpus-jsonl-file-parsed" />
        <StatBox label="Ingested" value={p.ingested.toLocaleString()} testid="corpus-jsonl-file-ingested" />
        <StatBox label="Skipped" value={p.skipped.toLocaleString()} testid="corpus-jsonl-file-skipped" />
        <StatBox label="Failed" value={p.failed.toLocaleString()} testid="corpus-jsonl-file-failed" />
        <StatBox
          label={running ? "ETA" : "Elapsed"}
          value={
            running && etaSec > 0
              ? formatSeconds(etaSec)
              : p.elapsedMs
                ? formatSeconds(Math.round(p.elapsedMs / 1000))
                : "—"
          }
          testid="corpus-jsonl-file-eta"
        />
      </div>

      {p.lastError && (
        <div
          className="border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-400 font-ui"
          data-testid="corpus-jsonl-file-error"
        >
          Last error: {p.lastError}
        </div>
      )}

      <p className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)]/70">
        {rate > 0 ? `${rate} rows/sec` : "waiting to start"} · Batch size {BATCH_SIZE} · Retries {MAX_RETRIES_PER_BATCH} per batch
      </p>
    </div>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="border border-[var(--border)] px-3 py-2">
      <div className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
        {label}
      </div>
      <div className="font-mono-ui text-sm text-[var(--text)] mt-1 break-words" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function formatSeconds(s) {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m < 60) return `${m}m ${sec}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
